import React, {useEffect, useState} from 'react'
import {Button} from "primereact/button";
import {Chart} from "primereact/chart";
import StatistikGamesDialog from "../tournaments/StatistikGamesDialog";
import {AUFNAHMEN_BEREICHE, FARBEN, getAchsen, getDiagrammOptionen, legQuote, vorhanden, zaehleWerte, zahl} from "./statistikWerte";

/**
 * Details zu einem Spieler (oder zum ganzen Event) im neuen Design, als Vollbild über der Seite.
 * Ersetzt in der neuen Ansicht den StatistikDialog mit reinem Text.
 *
 * @param mitBilanz Spiel- und Legbilanz anzeigen, für das Event nicht sinnvoll
 * @param playerId wenn gesetzt, können die einzelnen Spiele angesehen werden
 */
export default function StatistikSpielerDialog({name, statistik, mitBilanz, tournamentId, playerId, onClose}) {

    const [spieleOffen, setSpieleOffen] = useState(false);

    useEffect(() => {
        // die Seite dahinter soll nicht mitscrollen
        const overflowVorher = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = overflowVorher;
        };
    }, []);

    useEffect(() => {
        // solange die Spiele offen sind, schließt Escape nur diese
        if (spieleOffen) {
            return;
        }
        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                onClose();
            }
        };
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [spieleOffen, onClose]);

    const kennzahl = (wert, bezeichnung) => {
        return <div className="dhpt-kennzahl"><b>{wert}</b><small>{bezeichnung}</small></div>
    }

    const getBilanzDiagramm = (titel, werte) => {
        const gesamt = werte.reduce((summe, w) => summe + w.wert, 0);
        if (gesamt === 0) {
            return <div className="dhpt-st-ring">
                <h4 className="dhpt-st-untertitel">{titel}</h4>
                <p className="dhpt-hinweis">Noch nichts gespielt.</p>
            </div>
        }
        const data = {
            labels: werte.map(w => w.label),
            datasets: [{data: werte.map(w => w.wert), backgroundColor: werte.map(w => w.farbe), borderColor: "#0a3a4c", borderWidth: 2}],
        };
        const beschreibung = werte.map(w => w.label + " " + w.wert).join(", ");
        return <div className="dhpt-st-ring">
            <h4 className="dhpt-st-untertitel">{titel}</h4>
            <div className="dhpt-st-ring-diagramm" role="img" aria-label={titel + ": " + beschreibung}>
                <Chart type="doughnut" data={data} style={{height: "100%"}}
                       options={getDiagrammOptionen({plugins: {legend: {display: false}}, weitere: {cutout: "65%"}})}/>
            </div>
            <ul className="dhpt-st-legende">
                {werte.map(w => <li key={w.label}><span className="dhpt-st-legende-punkt" style={{backgroundColor: w.farbe}}/>{w.label} <b>{w.wert}</b></li>)}
            </ul>
        </div>
    }

    const getBilanz = () => {
        const quote = legQuote(statistik);
        const spiele = [
            {label: "Gewonnen", wert: statistik.gewonneneSpiele, farbe: FARBEN.gewonnen},
            {label: "Verloren", wert: statistik.verloreneSpiele, farbe: FARBEN.verloren},
        ];
        if (statistik.unentschiedenSpiele > 0) {
            spiele.push({label: "Unentschieden", wert: statistik.unentschiedenSpiele, farbe: FARBEN.unentschieden});
        }
        return <section className="dhpt-karte">
            <h3 className="dhpt-karte-titel"><span>Bilanz</span>{quote !== undefined && <span className="dhpt-karte-zusatz">Leg-Quote {zahl(quote, 0)} %</span>}</h3>
            <div className="dhpt-st-ringe">
                {getBilanzDiagramm("Spiele", spiele)}
                {getBilanzDiagramm("Legs", [
                    {label: "Gewonnen", wert: statistik.gewonneneLegs, farbe: FARBEN.gewonnen},
                    {label: "Verloren", wert: statistik.verloreneLegs, farbe: FARBEN.verloren},
                ])}
            </div>
        </section>
    }

    const getVerteilung = () => {
        const werte = AUFNAHMEN_BEREICHE.map(bereich => statistik[bereich.key] || 0);
        if (werte.every(wert => wert === 0)) {
            return null;
        }
        const data = {
            labels: AUFNAHMEN_BEREICHE.map(bereich => bereich.label),
            datasets: [{label: "Aufnahmen", data: werte, backgroundColor: FARBEN.spielerB, borderRadius: 4}],
        };
        return <section className="dhpt-karte">
            <h3 className="dhpt-karte-titel"><span>Verteilung der Aufnahmen</span><span className="dhpt-karte-zusatz">geworfene Punkte je Aufnahme</span></h3>
            <div className="dhpt-st-balken-diagramm" role="img"
                 aria-label={"Verteilung der Aufnahmen: " + AUFNAHMEN_BEREICHE.map((bereich, i) => bereich.label + ": " + werte[i]).join(", ")}>
                <Chart type="bar" data={data} style={{height: "100%"}}
                       options={getDiagrammOptionen({plugins: {legend: {display: false}}, scales: getAchsen("Anzahl")})}/>
            </div>
        </section>
    }

    const getWerteListe = (titel, werte) => {
        const gezaehlt = zaehleWerte(werte);
        if (gezaehlt.length === 0) {
            return null;
        }
        return <section className="dhpt-karte">
            <h3 className="dhpt-karte-titel"><span>{titel}</span></h3>
            <ul className="dhpt-st-werte">
                {gezaehlt.map(eintrag => <li key={eintrag.wert} className="dhpt-chip">
                    {eintrag.anzahl > 1 && <span className="dhpt-st-anzahl">{eintrag.anzahl}×</span>}{eintrag.wert}
                </li>)}
            </ul>
        </section>
    }

    return <div className="dhpt-vollbild" role="dialog" aria-modal="true" aria-label={"Statistik " + name}>
        <div className="dhpt-vollbild-leiste">
            <span className="dhpt-vollbild-titel">{name}</span>
            {vorhanden(playerId) && vorhanden(tournamentId) &&
                <Button icon="pi pi-list" label="Spiele" className="p-button-sm p-button-outlined dhpt-button-hell" onClick={() => setSpieleOffen(true)}/>
            }
            <Button icon="pi pi-times" label="Schließen" className="p-button-sm p-button-secondary" onClick={() => onClose()}/>
        </div>
        <div className="dhpt-vollbild-inhalt">
            <div className="dhpt-st-dialog">
                <div className="dhpt-kennzahlen">
                    {kennzahl(zahl(statistik.average), "Average")}
                    {kennzahl(zahl(statistik.first9Average), "First 9 Average")}
                    {kennzahl(vorhanden(statistik.bestLeg) ? statistik.bestLeg : "–", "Best Leg (Darts)")}
                    {kennzahl(vorhanden(statistik.worstLeg) ? statistik.worstLeg : "–", "Worst Leg (Darts)")}
                    {kennzahl(statistik._100Plus, "100+")}
                    {kennzahl(statistik._140Plus, "140+")}
                    {kennzahl(statistik._171er, "171")}
                    {kennzahl(statistik._180er, "180")}
                    {kennzahl(zahl(statistik.anzahlWuerfeGesamt, 0), "Würfe")}
                    {kennzahl(zahl(statistik.anzahlGeworfenerPunkteGesamt, 0), "Geworfene Punkte")}
                </div>
                {mitBilanz && getBilanz()}
                {getVerteilung()}
                <div className="dhpt-karten">
                    {getWerteListe("Highscores", statistik.highscores)}
                    {getWerteListe("Highfinishes", statistik.highfinishes)}
                </div>
            </div>
        </div>
        {spieleOffen && <StatistikGamesDialog tournamentId={tournamentId} playerId={playerId} onClose={() => setSpieleOffen(false)}/>}
    </div>
}
